import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import apiService from '@/services/apiService';
import type { ConfigData } from '@/types/app';

interface PasswordGateProps {
	labelName: string;
	onAuthenticated: (token: string, config: ConfigData) => void;
}

/**
 * Password gate component for password-protected white labels.
 *
 * Prompts the user to enter a password to access
 * the white-label configuration.
 */
const PasswordGate = ({ labelName, onAuthenticated }: PasswordGateProps): React.JSX.Element => {
	const { t } = useTranslation();
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			// Authenticate with label password using apiService
			const authData = await apiService.auth.labelPassword(password);

			// Validate response structure
			if (!authData.token || !authData.label_slug) {
				throw new Error('Invalid authentication response. Please try again.');
			}

			const { token, label_slug } = authData;

			// Store the token
			localStorage.setItem(`quotr_label_auth_${label_slug}`, token);

			// Fetch config with the authentication token
			const config = await apiService.config.fetchWithAuth(token, window.location.href);

			onAuthenticated(token, config);
		} catch (err) {
			// Ensure error message is always a string
			const errorMessage =
				err instanceof Error ? err.message : String(err) || 'An unexpected error occurred';
			setError(errorMessage);
			setIsLoading(false);
		}
	};

	return (
		<div
			className="flex items-center justify-center min-h-screen bg-gray-100"
			data-testid="password-gate"
		>
			<div
				className="bg-white rounded-lg border border-border mb-5 p-8 pb-10 mb-12"
				style={{ maxWidth: '440px', width: '100%' }}
			>
				<div className="mb-6">
					<h1 className="text-xl font-bold mb-2">{labelName}</h1>
					<p className="text-muted-foreground">{t('common.passwordGate.title')}</p>
				</div>

				<form onSubmit={handleSubmit}>
					<div className="mb-3">
						<label htmlFor="password" className="block text-sm font-medium mb-2">
							{t('common.passwordGate.passwordLabel')}
						</label>
						<input
							type="password"
							className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
								error ? 'border-red-500' : 'border-gray-300'
							}`}
							id="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="••••••••••••"
							required
							disabled={isLoading}
							data-testid="password-input"
						/>
						{error && (
							<div className="text-sm text-red-500 mt-1" data-testid="error-message">
								{t('common.passwordGate.genericErrorMessage')}
							</div>
						)}
					</div>

					<Button
						type="submit"
						variant="default"
						size="default"
						className="w-full px-4 py-2 font-medium"
						disabled={isLoading || !password}
						loading={isLoading}
						loadingText={t('common.passwordGate.authenticating')}
						data-testid="submit-button"
					>
						{t('common.passwordGate.submitButton')}
					</Button>
				</form>
			</div>
		</div>
	);
};

export default PasswordGate;
